import { useExamCenter } from '@exam-center/exam-center-details';
import { useGetExamCenterEventSummariesQuery } from '@exam-center/graphql';
import {
  Box,
  ChairAltOutlinedIcon,
  EventAvailableOutlinedIcon,
  Flex,
  HowToRegOutlinedIcon,
  Grid,
  GroupOutlinedIcon,
  PaperSection,
  type PaperSectionProps,
  Text,
  TotalSummaryCard,
  NoAccountsOutlinedIcon,
  Selection,
} from '@shared/components';
import { dayjs, formatNumber } from '@shared/utils';
import { useMemo } from 'react';

import { useExamEventStatsSearchParams, type ExamEventStatsPeriod } from '../../hooks';

import {
  getExamEventStatsSummaryTitle,
  getFilterParamsByExamEventStatsPeriod,
  getExamEventStatsSummaryData,
} from './utils';

type ExamEventStatsSectionProps = Omit<PaperSectionProps, 'title' | 'endAdornment'>;

const PRIOR_YEAR_LIMIT = 2;
const WIDTH_TO_SWITCH_LAYOUT = 632;

export default function ExamEventStatsSection(props: Readonly<ExamEventStatsSectionProps>) {
  const { examCenter } = useExamCenter();
  const now = dayjs();

  const [{ period }, { changePeriod }] = useExamEventStatsSearchParams();

  const yearOptions = [...new Array(PRIOR_YEAR_LIMIT + 1)].map(
    (_, index) => now.get('year') - index,
  );

  const { startedAt, endedAt } = getFilterParamsByExamEventStatsPeriod(period);

  const { data: examCenterEventSummariesData, loading: isExamCenterEventSummariesLoading } =
    useGetExamCenterEventSummariesQuery({
      variables: {
        getExamCenterEventSummariesInput: {
          examCenterCode: examCenter.code,
          startedAt,
          endedAt,
        },
      },
    });

  const {
    publicRoundCount,
    inHouseRoundCount,
    roundCount,
    seatCount,
    publicSeatCount,
    inHouseSeatCount,
    examineeCount,
    publicExamEventExamineeCount,
    inHouseExamEventExamineeCount,
    attendedExamineeCount,
    publicAttendedExamineeCount,
    inHouseAttendedExamineeCount,
    absentExamineeCount,
    publicAbsentExamineeCount,
    inHouseAbsentExamineeCount,
  } = useMemo(
    () => getExamEventStatsSummaryData(examCenterEventSummariesData),
    [examCenterEventSummariesData],
  );

  return (
    <PaperSection
      title='รอบสอบทั้งหมด'
      endAdornment={
        <Selection
          sx={{ width: '200px' }}
          value={period}
          onChange={(event) => changePeriod(event.target.value as ExamEventStatsPeriod)}
          options={[
            { label: 'เดือนนี้', value: 'current-month' },
            { label: '3 เดือนล่าสุด', value: 'last-3-months' },
            { label: '6 เดือนล่าสุด', value: 'last-6-months' },
            ...yearOptions.map((year) => ({ label: `ปี ${year.toString()}`, value: year })),
          ]}
        />
      }
      {...props}
    >
      <Flex
        sx={(theme) => ({
          flexDirection: 'column',
          gap: theme.spacing(4),
          padding: theme.spacing(6),
        })}
      >
        <Text>{getExamEventStatsSummaryTitle(period)}</Text>
        <Grid
          sx={(theme) => ({
            gap: theme.spacing(4),
            gridTemplateColumns: 'repeat(6, 1fr)',
            containerName: 'exam-event-stats-grid',
            containerType: 'inline-size',
            '& > .MuiBox-root': { gridColumn: 'span 3' },
            // set column area for fourth summary card
            // set column area for last summary card
            '& > .MuiBox-root:last-of-type': { gridColumn: '1 / -1' },
            // Layout when container width > WIDTH_TO_SWITCH_LAYOUT px
            [`@container exam-event-stats-grid (width > ${WIDTH_TO_SWITCH_LAYOUT}px)`]: {
              // set column area for first to third summary card
              '& > .MuiBox-root:nth-of-type(n+1):nth-of-type(-n+3)': { gridColumn: 'span 2' },
              '& > .MuiBox-root:last-of-type': { gridColumn: 'span 3' },
            },
          })}
        >
          <Box>
            <TotalSummaryCard
              icon={<EventAvailableOutlinedIcon color='primary' fontSize='large' />}
              slotProps={{
                iconContainer: {
                  sx: (theme) => ({ backgroundColor: theme.palette.primary.lighter }),
                },
                labelContainer: {
                  sx: { minWidth: '125px' },
                },
              }}
              loading={isExamCenterEventSummariesLoading}
              label='รอบสอบทั้งหมด'
              value={`${formatNumber(roundCount)} รอบ`}
              statItems={[
                {
                  label: 'Public',
                  value: formatNumber(publicRoundCount),
                },
                {
                  label: 'In-house',
                  value: formatNumber(inHouseRoundCount),
                },
              ]}
            />
          </Box>
          <Box>
            <TotalSummaryCard
              icon={
                <ChairAltOutlinedIcon sx={{ color: 'hsla(262, 52%, 47%, 1)' }} fontSize='large' />
              }
              slotProps={{
                iconContainer: {
                  sx: { backgroundColor: 'hsla(264, 45%, 94%, 1)' },
                },
                labelContainer: {
                  sx: { minWidth: '125px' },
                },
              }}
              loading={isExamCenterEventSummariesLoading}
              label='จำนวนเปิดรับสมัคร'
              value={`${formatNumber(seatCount)} ที่นั่ง`}
              statItems={[
                {
                  label: 'Public',
                  value: formatNumber(publicSeatCount),
                },
                {
                  label: 'In-house',
                  value: formatNumber(inHouseSeatCount),
                },
              ]}
            />
          </Box>
          <Box>
            <TotalSummaryCard
              icon={<GroupOutlinedIcon color='info' fontSize='large' />}
              slotProps={{
                iconContainer: {
                  sx: (theme) => ({ backgroundColor: theme.palette.info.lighter }),
                },
                labelContainer: {
                  sx: { minWidth: '125px' },
                },
              }}
              loading={isExamCenterEventSummariesLoading}
              label='จำนวนผู้สมัครสอบ'
              value={`${examineeCount} คน`}
              statItems={[
                {
                  label: 'Public',
                  value: formatNumber(publicExamEventExamineeCount),
                },
                {
                  label: 'In-house',
                  value: formatNumber(inHouseExamEventExamineeCount),
                },
              ]}
            />
          </Box>
          <Box
            sx={{
              gridColumn: 'span 3',
            }}
          >
            <TotalSummaryCard
              icon={<HowToRegOutlinedIcon color='success' fontSize='large' />}
              slotProps={{
                iconContainer: {
                  sx: (theme) => ({ backgroundColor: theme.palette.success.lighter }),
                },
                labelContainer: {
                  sx: { minWidth: '125px' },
                },
              }}
              loading={isExamCenterEventSummariesLoading}
              label='จำนวนผู้เข้าสอบ'
              value={`${formatNumber(attendedExamineeCount)} คน`}
              statItems={[
                {
                  label: 'Public',
                  value: formatNumber(publicAttendedExamineeCount),
                },
                {
                  label: 'In-house',
                  value: formatNumber(inHouseAttendedExamineeCount),
                },
              ]}
            />
          </Box>
          <Box>
            <TotalSummaryCard
              icon={<NoAccountsOutlinedIcon color='error' fontSize='large' />}
              slotProps={{
                iconContainer: {
                  sx: (theme) => ({ backgroundColor: theme.palette.error.lighter }),
                },
              }}
              loading={isExamCenterEventSummariesLoading}
              label='จำนวนผู้ขาดสอบ'
              value={`${formatNumber(absentExamineeCount)} คน`}
              statItems={[
                {
                  label: 'Public',
                  value: formatNumber(publicAbsentExamineeCount),
                },
                {
                  label: 'In-house',
                  value: formatNumber(inHouseAbsentExamineeCount),
                },
              ]}
            />
          </Box>
        </Grid>
      </Flex>
    </PaperSection>
  );
}
